import type { RecommendationStore } from '@/stores/recommendation/recommendation';
import type { MagicVariables } from '@/types';

import type { RecommendationClickPayload } from './recommendations';
import { sendRecommendationClick } from './recommendations';
import type { SendVideoClickParams } from './types';

export type VideoClickList = 'main' | 'related_right' | 'related_bottom' | 'playlist';

export class BaseVideoClickTracker implements MagicVariables {
    $root: MagicVariables['$root'];
    $refs: MagicVariables['$refs'];
    videoItems: HTMLElement[];
    recommendationStore: RecommendationStore;

    constructor(recommendationStore: RecommendationStore) {
        this.recommendationStore = recommendationStore;
    }

    init($root: MagicVariables['$root'], $refs?: MagicVariables['$refs']) {
        this.$root = $root;
        this.$refs = $refs;
        this.getVideoItems();
        this.setupVideoItemClickListener();
        this.setupPopunderListener();
    }

    setupVideoItemClickListener() {
        this.videoItems.forEach((videoItem) => {
            videoItem.addEventListener('click', this.onVideoItemClick.bind(this));
        });
    }

    setupPopunderListener() {
        window.addEventListener('popunder_triggered', this.onPopunderTriggered.bind(this));
    }

    onPopunderTriggered(event: CustomEvent) {
        const videoItem = event.detail.target as HTMLElement;

        // A pop-ad-triggering click is not stopPropagation()'d, so it also
        // bubbles to the per-item click listener, which already emits video_click
        // for items in this.videoItems. Emit here only for targets the click path
        // cannot reach, to avoid a double video_click for one user action. The
        // legacy recommendation path below is unchanged (it dedupes downstream).
        if (!this.videoItems.includes(videoItem)) {
            this._emitVideoClick(videoItem);
        }

        const trackingData = this.getTrackingData(videoItem);

        if (!this.isTrackingDataValid(trackingData)) {
            return;
        }

        this.recommendationStore.addItem({
            id: trackingData.videoId.toString(),
            recommendedBy: this.recommendationStore.getRecommendationId(),
        });
    }

    onVideoItemClick(event: MouseEvent) {
        const videoItem = event.currentTarget as HTMLElement;
        this._emitVideoClick(videoItem);

        const trackingData = this.getTrackingData(videoItem);

        if (!this.isTrackingDataValid(trackingData)) {
            return;
        }

        const recommendationId = this.recommendationStore.getRecommendationId();

        this.recommendationStore.addItem({
            id: trackingData.videoId.toString(),
            recommendedBy: recommendationId,
        });

        this.handleVideoItemClick(trackingData, recommendationId);
    }

    // Default implementation - can be overridden by subclasses if needed
    getTrackingData(videoItem: HTMLElement): SendVideoClickParams {
        const itemPosition = this._getVideoItemPosition(videoItem);
        const videoId = this._getVideoItemId(videoItem);
        const pageNumber = 1;

        return {
            videoId,
            pageNumber,
            itemPosition,
        };
    }

    // Default implementation - can be overridden by subclasses if needed
    isTrackingDataValid(trackingData: SendVideoClickParams): boolean {
        if (trackingData.videoId < 0 || trackingData.itemPosition < 1) {
            return false;
        }

        return true;
    }

    // Default implementation - can be overridden by subclasses if needed
    getVideoItems() {
        this.videoItems = Array.from(this.$root.querySelectorAll('.js-video-item'));

        // .video-item and .js-video-item are still both used to identify video items.
        if (!this.videoItems.length) {
            this.videoItems = Array.from(this.$root.querySelectorAll('.video-item'));
        }
    }

    // Default implementation - can be overridden by subclasses if needed
    handleVideoItemClick(trackingData: SendVideoClickParams, recommendationId: string) {
        const payload: RecommendationClickPayload = {
            video_id: trackingData.videoId,
            page_number: trackingData.pageNumber,
            item_position: trackingData.itemPosition,
        };

        if (recommendationId) {
            payload.recommendation_id = recommendationId;
        }

        this.sendRecommendationClickEvent(payload);
    }

    handleRecommendationsState(videoId: string) {
        const recommendationId = this.recommendationStore.findSourceRecommendationId(videoId);

        if (!recommendationId) {
            this.reportMissingRecommendationId();
            return;
        }

        return recommendationId;
    }

    reportMissingRecommendationId() {
        console.error('No recommendationId has been found');
    }

    /**
     * Returns the position of the video item, starting from 1.
     *
     * @returns {number} - The position of the video item in the list.
     */
    _getVideoItemPosition(videoItem: HTMLElement): number {
        return Array.from(this.videoItems).indexOf(videoItem) + 1;
    }

    _getVideoItemId(videoItem: HTMLElement): number {
        return Number(videoItem.getAttribute('data-id'));
    }

    // Runs before, and independently of, the GA/recommendation path that
    // subclasses override, so it validates its own inputs rather than reusing
    // isTrackingDataValid.
    _emitVideoClick(videoItem: HTMLElement): void {
        // Reject a missing/empty data-id here rather than in _getVideoItemId:
        // Number(null) and Number('') are both 0, which would emit a bogus
        // item_id:0, but _getVideoItemId is shared with the untouched legacy
        // recommendation/GA paths, so the guard stays local to video_click.
        const rawId = videoItem.getAttribute('data-id');
        if (rawId === null || rawId === '') {
            return;
        }

        const item_id = this._getVideoItemId(videoItem);

        if (Number.isNaN(item_id) || item_id < 0) {
            return;
        }

        const position = this._getVideoItemPosition(videoItem);

        if (position < 1) {
            return;
        }

        const list = this._getVideoClickList(videoItem);

        window.sbTracker?.track('video_click', { item_id, position, list });
    }

    // Derived from the item's ancestor, not $root: the video page renders both
    // related lists under one $root, so only the ancestor can disambiguate them.
    _getVideoClickList(videoItem: HTMLElement): VideoClickList {
        if (videoItem.closest('.js-related-videos-right')) {
            return 'related_right';
        }

        if (videoItem.closest('.js-related-videos-bottom')) {
            return 'related_bottom';
        }

        // The id-based #playlist-list is the video-page playlist (video_playlist.html).
        // The separate class-based .playlist-list surfaces live on pages this tracker
        // does not cover, so they correctly fall through to 'main'.
        if (videoItem.closest('#playlist-list')) {
            return 'playlist';
        }

        return 'main';
    }

    /**
     * Send data to backend
     */
    sendRecommendationClickEvent(payload: RecommendationClickPayload) {
        sendRecommendationClick(payload);
    }
}
